import { useTranslation } from 'react-i18next';
import { IconButton } from 'qampo-ui-library';
import { IconChevronDown } from '../../UI';
import type { WeekNavigation } from '../../../types/weekOverview';
import styles from './CalendarWeekNavBar.module.css';

interface CalendarWeekNavBarProps {
  weekNumber: number;
  weekRangeLabel: string;
  canPrint: boolean;
  // Omitted for a fixed week: the prev/today/next cluster then disappears,
  // while the week label and print button stay.
  navigation?: WeekNavigation;
  onPrint: () => void;
}

export const CalendarWeekNavBar = ({
  weekNumber,
  weekRangeLabel,
  canPrint,
  navigation,
  onPrint
}: CalendarWeekNavBarProps) => {
  const { t } = useTranslation();

  return (
    <div className={`${styles.weekNavBar} js-print-hide`}>
      <div className={styles.weekNavLabel}>
        <span className={styles.weekNavWeek}>
          {t('week-overview-week-label', { weekNumber })}
        </span>
        <span className={styles.weekNavRange}>{weekRangeLabel}</span>
      </div>
      <div className={styles.weekNavRight}>
        {canPrint && (
          <button type="button" className={styles.printButton} onClick={onPrint}>
            <svg
              className={styles.printIcon}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              aria-hidden="true"
            >
              <path d="M4 6V2.5h8V6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 12H2.5V6.5h11V12H12" strokeLinecap="round" strokeLinejoin="round" />
              <rect x="4" y="10" width="8" height="3.5" rx="0.75" />
            </svg>
            {t('week-overview-print')}
          </button>
        )}
        {navigation && (
          <div className={styles.weekNav}>
            <span
              aria-label={t('week-overview-previous-week')}
              title={t('week-overview-previous-week')}
            >
              <IconButton
                icon={<IconChevronDown className={styles.navIconLeft} />}
                onClick={navigation.onPreviousWeek}
                size="default"
                iconColor="var(--color-text-primary)"
                buttonBgColor="var(--color-surface-primary)"
                classes={styles.navButton}
              />
            </span>
            <button
              type="button"
              className={styles.todayButton}
              onClick={navigation.onGoToToday}
              disabled={navigation.isViewingCurrentWeek}
            >
              {t('week-overview-today')}
            </button>
            <span aria-label={t('week-overview-next-week')} title={t('week-overview-next-week')}>
              <IconButton
                icon={<IconChevronDown className={styles.navIconRight} />}
                onClick={navigation.onNextWeek}
                size="default"
                iconColor="var(--color-text-primary)"
                buttonBgColor="var(--color-surface-primary)"
                classes={styles.navButton}
              />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
